import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import AdminBillingPage from './AdminBillingPage';
import UsersPanel from './admin/UsersPanel';
import AnalyticsPanel from './admin/AnalyticsPanel';
import SearchIntelPanel from './admin/SearchIntelPanel';
import LeadsPanel from './admin/LeadsPanel';
import EmailPanel from './admin/EmailPanel';
import ContentPanel from './admin/ContentPanel';
import UniversitiesPanel from './admin/UniversitiesPanel';
import SystemPanel from './admin/SystemPanel';
import ChatStatsPanel from './admin/ChatStatsPanel';
import LabsPanel from './admin/LabsPanel';
import User360Panel from './admin/User360Panel';
import DatabasePanel from './admin/DatabasePanel';
import PostHogPanel from './admin/PostHogPanel';
import OceanPanel from './admin/OceanPanel';
import AuditPanel from './admin/AuditPanel';
import FunnelPanel from './admin/FunnelPanel';
import IpIntelPanel from './admin/IpIntelPanel';
import TechPagesPanel from './admin/TechPagesPanel';
import HorizonPanel from './admin/HorizonPanel';
import AwsPanel from './admin/AwsPanel';
import UserTagsPanel from './admin/UserTagsPanel';
import HomepagePanel from './admin/HomepagePanel';
import OnboardingPanel from './admin/OnboardingPanel';
import ForYouWhatsNewPanel from './admin/ForYouWhatsNewPanel';
import RecrawlPanel from './admin/recrawl/RecrawlPanel';
import AccessControlPanel from './admin/AccessControlPanel';
import CommandPalette, { PALETTE_SHORTCUT, openCommandPalette } from './admin/CommandPalette';
import { Btn, panelGet, panelPost } from './admin/kit';
import { ADMIN_NAV_EVENT, type AdminTarget, type AdminTargetIn, adminHref, parseAdminTarget } from './admin/adminNav';
import { ADMIN_TABS } from './admin/adminTabs';
import {
  ACCESS_GROUP, ADMIN_ACCESS_CHANGED_EVENT, AdminAccessProvider, NotEnabled, OPEN_ACCESS,
  parseViews, type AdminAccess,
} from './admin/adminAccess';

/**
 * /admin — the whole admin panel, migrated from the Streamlit tools
 * (admin_panel_v2.py, admin_v3/) group by group.
 *
 * The shell owns three things and nothing else:
 *  1. The gate: the normal Cosmos login, admin-ness decided server-side by
 *     the billing_admins allowlist (same as the billing admin always used).
 *  2. The group nav: fetched from GET /api/v1/admin/views, so groups can be
 *     retitled/reordered server-side without touching this file.
 *  3. View preferences: every admin can hide groups they never use — the
 *     old panel's dozen tabs is exactly why. Hidden sets are per-admin,
 *     stored server-side, and reversible from the Customize menu.
 *  4. Deep links (admin/adminNav.tsx): /admin?group=<key>&tab=…&<params>
 *     opens that group and hands the rest to its panel — read on load and on
 *     every in-app navigation, so panels can link into each other and Back
 *     returns to the group the admin came from.
 *  5. Tab-wise access (admin/adminAccess.tsx): admin/views lists only the
 *     groups this admin may open (super admins: all of them) plus which
 *     controlled sub-tabs they may open. Every admin also gets Admins &
 *     access at the bottom of the nav (only super admins can change it). A
 *     deep link to a group or sub-tab this admin may not open shows "not
 *     enabled for your account" instead of the panel.
 *     Access is re-read when a super admin saves a rule here and when the
 *     server answers 403 admin_tab_not_enabled (access changed meanwhile).
 *
 * Each group's CONTENT is its own component; Billing embeds the previously
 * standalone AdminBillingPage rather than rebuilding it.
 */

type Gate = 'checking' | 'signed_out' | 'denied' | 'ok';

interface ViewGroup { key: string; title: string; description: string }

/** Every group this shell has a panel for (GroupContent), titled for the
 *  "not enabled" state of a group admin/views did not send. */
const STATIC_TITLES: Record<string, string> = (() => {
  const m: Record<string, string> = { funnel: 'Checkout funnel', audit: 'Audit log', [ACCESS_GROUP.key]: ACCESS_GROUP.title };
  ADMIN_TABS.forEach(t => { if (!m[t.group]) m[t.group] = t.label.split(' → ')[0]; });
  return m;
})();

/** Bookmarks to /admin?group=access&tab=features (the member-page controls
 *  now live in each group's own Feature access tab) open For You & What's New
 *  → Feature access. */
const OLD_FEATURES_TAB = 'features';
const FEATURE_ACCESS_TARGET: AdminTarget = { group: 'foryou_whatsnew', tab: 'feature_access' };

/** alerts/feed — see admin_alerts.feed(). One item per thing an admin may
 *  need to know about: deletion requests, refunds, ghost mandates, failed
 *  gateway cancels, rejected payments, new members, coupons used. */
type Severity = 'red' | 'amber' | 'info';
interface AlertItem {
  id: string; kind?: string; severity: Severity; group: string; title: string;
  detail?: string; when?: string | null; count?: number;
  /** Where to deal with it: a group and sub-tab, plus whatever that group's
   *  deep link understands — `user` (an account id) opens that account. */
  link?: AdminTarget;
  /** severity|when — what a resolution is pinned to (admin_alerts._fingerprint). */
  fingerprint?: string;
  /** Closed by an admin for everyone (alerts/resolve). Counts nowhere; it
   *  comes back by itself if the item gets worse or something new happens. */
  resolved?: { by: string; at?: string | null; note?: string };
}
interface AlertFeed {
  generated_at: string;
  /** Actionable (red + amber) items per group — the nav badges. */
  groups: Record<string, number>;
  totals?: Partial<Record<Severity | 'resolved', number>>;
  items: AlertItem[];
  /** The deletion module's own counts and switch, for the banner's footer line. */
  deletion?: {
    pending?: number; on_hold?: number; failed?: number; deleting?: number;
    due_now?: number; due_48h?: number; next_due_at?: string | null;
    auto_delete?: boolean; days?: number; scheduler_running?: boolean;
  };
  errors?: string[];
}

/**
 * "Seen" lives in this browser only (localStorage). It is a convenience for
 * the person at this screen — the bell stops counting what they have looked
 * at — never a record: the feed itself is what the server says is open, and
 * a red item stays in the banner however many times it has been seen.
 */
const SEEN_KEY = 'utech_admin_alerts_seen';
const loadSeen = (): Set<string> => {
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw.filter((x: any) => typeof x === 'string') : []);
  } catch { return new Set(); }
};
const saveSeen = (ids: Set<string>) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(ids).slice(-1000))); } catch { /* private mode */ }
};

/** "just now" / "12 min ago" / "3 h ago" / "2 d ago" / a date — the bell is
 *  read at a glance, and a full timestamp is in the title attribute. */
const ago = (iso?: string | null): string => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const DOT: Record<Severity, string> = {
  red: 'bg-red-600', amber: 'bg-amber-500', info: 'bg-primary-500',
};
const SeverityDot: React.FC<{ severity: Severity }> = ({ severity }) => (
  <span aria-hidden className={`inline-block w-2 h-2 rounded-full shrink-0 ${DOT[severity] || DOT.info}`} />
);

/** Red always makes the banner; amber only until it has been seen; info
 *  never; a resolved item never. */
const bannerItems = (feed: AlertFeed | null, seen: Set<string>): AlertItem[] =>
  (feed?.items || []).filter(it => !it.resolved
    && (it.severity === 'red' || (it.severity === 'amber' && !seen.has(it.id))));

/** Red and amber can be resolved by hand; info items only need seeing. */
const resolvable = (it: AlertItem) => !it.resolved && it.severity !== 'info' && !!it.fingerprint;

const BANNER_ITEMS = 4;

/**
 * Sits above every group's heading, not just the one the item belongs to: a
 * deletion request is a promise with a clock on it and a refund is money
 * that moved, and the old panel let both sit under the tenth sub-tab of one
 * group. Red when anything red is open (someone must act today), amber
 * otherwise. Everything else — info items, seen amber ones — lives in the
 * bell so the banner stays short enough to be read.
 */
const AlertsBanner: React.FC<{
  feed: AlertFeed; seen: Set<string>; onOpen: (it: AlertItem) => void;
  onSeeAll: () => void; onMarkAllSeen: () => void; onResolve: (it: AlertItem) => void;
}> = ({ feed, seen, onOpen, onSeeAll, onMarkAllSeen, onResolve }) => {
  const items = bannerItems(feed, seen);
  if (!items.length) return null;
  const reds = items.filter(it => it.severity === 'red').length;
  const urgent = reds > 0;
  // Same red as the page's own error box; the amber pair is the admin kit's Notice.
  const look = urgent
    ? 'text-red-800 bg-red-50 border-red-200'
    : 'text-amber-700 bg-amber-50 border-amber-200';
  const d = feed.deletion || {};
  const hasDeletion = items.some(it => it.group === 'users');
  return (
    <div role="alert" className={`text-sm rounded-lg border px-4 py-3 flex flex-col gap-2 ${look}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="font-bold">
            {items.length} alert{items.length === 1 ? '' : 's'} need{items.length === 1 ? 's' : ''} attention
          </span>
          {reds > 0 && <span> · {reds} urgent</span>}
          {items.length - reds > 0 && <span> · {items.length - reds} to look at</span>}
        </div>
        <div className="flex gap-2">
          <Btn small onClick={onMarkAllSeen} title="Amber and info items stop counting; red ones stay until resolved (Resolve, beside each)">
            Mark all seen
          </Btn>
          <Btn small kind="primary" onClick={() => onOpen(items[0])}>Review →</Btn>
        </div>
      </div>
      <ul className="flex flex-col gap-0.5 text-xs">
        {items.slice(0, BANNER_ITEMS).map(it => (
          <li key={it.id} className="flex items-center gap-2 min-w-0">
            <SeverityDot severity={it.severity} />
            <button onClick={() => onOpen(it)} title={it.detail || undefined}
              className="truncate text-left hover:underline min-w-0">
              <span className="font-medium">{it.title}</span>
              {it.when && <span className="opacity-70"> · {ago(it.when)}</span>}
            </button>
            {resolvable(it) && (
              <button onClick={() => onResolve(it)}
                title="Settled already? Close it for every admin, with a note"
                className="shrink-0 ml-auto underline opacity-80 hover:opacity-100">
                Resolve
              </button>
            )}
          </li>
        ))}
        {items.length > BANNER_ITEMS && (
          <li>
            <button onClick={onSeeAll} className="opacity-70 hover:underline">
              +{items.length - BANNER_ITEMS} more — open the bell
            </button>
          </li>
        )}
      </ul>
      {hasDeletion && (
        <div className="text-xs opacity-80">
          {d.auto_delete ? `Auto-delete is ON (${d.days} days)` : 'Auto-delete is OFF'}
          {d.auto_delete && d.scheduler_running === false
            ? ' — but the scheduler is not running, so nothing is deleted until it is.' : ''}
        </div>
      )}
    </div>
  );
};

/**
 * The bell: every item, seen or not, newest urgent first, with the count of
 * unseen ones on the badge. A dropdown rather than a page — the admin is in
 * the middle of something and wants to glance, then either jump or go back.
 */
const AlertsBell: React.FC<{
  feed: AlertFeed | null; seen: Set<string>; open: boolean; setOpen: (v: boolean) => void;
  onOpen: (it: AlertItem) => void; onMarkAllSeen: () => void; onRefresh: () => void;
  err: string;
  /** The item whose "Resolve" note box is open, if any. */
  resolving: string | null; setResolving: (id: string | null) => void;
  /** Resolves with '' on success, else the error to show. */
  onResolve: (it: AlertItem, note: string) => Promise<string>;
  onUnresolve: (it: AlertItem) => Promise<string>;
}> = ({ feed, seen, open, setOpen, onOpen, onMarkAllSeen, onRefresh, err,
        resolving, setResolving, onResolve, onUnresolve }) => {
  const all = feed?.items || [];
  const items = all.filter(it => !it.resolved);
  const done = all.filter(it => it.resolved);
  const unseen = items.filter(it => !seen.has(it.id)).length;
  const anyRed = items.some(it => it.severity === 'red' && !seen.has(it.id));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [actErr, setActErr] = useState<{ id: string; msg: string } | null>(null);
  // A fresh note box each time one is opened.
  useEffect(() => { setNote(''); setActErr(null); }, [resolving]);

  const confirm = async (it: AlertItem) => {
    setBusy(it.id); setActErr(null);
    const e = await onResolve(it, note.trim());
    setBusy('');
    if (e) setActErr({ id: it.id, msg: e });
  };
  const undo = async (it: AlertItem) => {
    setBusy(it.id); setActErr(null);
    const e = await onUnresolve(it);
    setBusy('');
    if (e) setActErr({ id: it.id, msg: e });
  };
  const where = (it: AlertItem) => ((it.link?.group || it.group) === 'users' ? 'Users' : 'Billing');

  return (
    <>
      <button onClick={() => setOpen(!open)} aria-label={`Alerts: ${unseen} unseen`}
        title={items.length ? `${items.length} alert(s), ${unseen} unseen` : 'No alerts'}
        className="relative shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-grayscale-1200 hover:bg-grayscale-200">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unseen > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 rounded-full text-white text-[10px] font-bold px-1.5 leading-4
            ${anyRed ? 'bg-red-600' : 'bg-amber-500'}`}>
            {unseen > 99 ? '99+' : unseen}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-[80]" onClick={() => { setOpen(false); setResolving(null); }}>
          <div onClick={e => e.stopPropagation()}
            className="absolute top-14 left-4 w-[26rem] max-w-[calc(100vw-2rem)] max-h-[80vh] flex flex-col
                       bg-white rounded-2xl shadow-lg border border-grayscale-300">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-grayscale-300">
              <div>
                <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Alerts</div>
                <div className="text-[11px] text-grayscale-1100">
                  {items.length ? `${items.length} open · ${unseen} unseen` : 'Nothing open'}
                  {done.length ? ` · ${done.length} resolved` : ''}
                  {feed?.generated_at ? ` · as of ${ago(feed.generated_at)}` : ''}
                </div>
              </div>
              <div className="flex gap-1.5">
                <Btn small onClick={onRefresh} title="Re-read the feed now">Refresh</Btn>
                <Btn small onClick={onMarkAllSeen} disabled={!unseen}>Mark all seen</Btn>
              </div>
            </div>
            {err && (
              <div className="px-4 py-2 text-[11px] text-red-700 bg-red-50 border-b border-red-200">
                Could not refresh: {err}
              </div>
            )}
            {feed?.errors && feed.errors.length > 0 && (
              <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-200"
                title={feed.errors.join('\n')}>
                Partial feed — {feed.errors.length} source(s) failed; the rest is current.
              </div>
            )}
            <ul className="overflow-y-auto flex flex-col divide-y divide-grayscale-200">
              {items.length === 0 && (
                <li className="px-4 py-6 text-sm text-grayscale-1100 text-center">Nothing needs attention.</li>
              )}
              {items.map(it => {
                const isSeen = seen.has(it.id);
                const boxOpen = resolving === it.id;
                return (
                  <li key={it.id} className="flex flex-col">
                    <div className="flex items-start">
                      <button onClick={() => onOpen(it)}
                        className={`flex-1 min-w-0 text-left px-4 py-2.5 flex gap-2.5 hover:bg-grayscale-100 ${isSeen ? 'opacity-70' : ''}`}>
                        <span className="pt-1.5"><SeverityDot severity={it.severity} /></span>
                        <span className="min-w-0 flex-1">
                          <span className={`block text-sm text-grayscale-1300 ${isSeen ? '' : 'font-bold'}`}>
                            {it.title}
                          </span>
                          {it.detail && (
                            <span className="block text-[11px] text-grayscale-1100 line-clamp-2">{it.detail}</span>
                          )}
                          <span className="block text-[11px] text-grayscale-1100 mt-0.5"
                            title={it.when || undefined}>
                            {where(it)}
                            {it.when ? ` · ${ago(it.when)}` : ''}
                            {isSeen ? ' · seen' : ''}
                          </span>
                        </span>
                      </button>
                      {resolvable(it) && !boxOpen && (
                        <button onClick={() => setResolving(it.id)}
                          title="Settled already? Close it for every admin, with a note"
                          className="shrink-0 mt-2 mr-3 text-[11px] font-medium text-grayscale-1200 underline hover:text-grayscale-1300">
                          Resolve
                        </button>
                      )}
                    </div>
                    {boxOpen && (
                      <div className="px-4 pb-3 flex flex-col gap-1.5">
                        <input autoFocus value={note} onChange={e => setNote(e.target.value)} maxLength={500}
                          onKeyDown={e => { if (e.key === 'Enter') confirm(it); if (e.key === 'Escape') setResolving(null); }}
                          placeholder="Note (optional), e.g. refunded in Razorpay on 24 Sept"
                          className="h-8 px-2.5 rounded-md border-[0.5px] border-primary-300 bg-white text-xs" />
                        <div className="flex items-center gap-1.5">
                          <Btn small kind="primary" disabled={busy === it.id} onClick={() => confirm(it)}>
                            {busy === it.id ? 'Resolving…' : 'Resolve for everyone'}
                          </Btn>
                          <Btn small onClick={() => setResolving(null)}>Cancel</Btn>
                          <span className="text-[10px] text-grayscale-1100">
                            Comes back if it gets worse or something new happens.
                          </span>
                        </div>
                        {actErr?.id === it.id && <span className="text-[11px] text-red-700">{actErr.msg}</span>}
                      </div>
                    )}
                  </li>
                );
              })}
              {done.length > 0 && (
                <li className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-grayscale-1100 bg-grayscale-100">
                  Resolved
                </li>
              )}
              {done.map(it => (
                <li key={it.id} className="px-4 py-2 flex items-start gap-2.5 opacity-70">
                  <span className="pt-1.5"><SeverityDot severity={it.severity} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-grayscale-1300 line-through decoration-grayscale-600">
                      {it.title}
                    </span>
                    <span className="block text-[11px] text-grayscale-1100 mt-0.5"
                      title={it.resolved?.at || undefined}>
                      Resolved by {it.resolved?.by || 'an admin'}
                      {it.resolved?.at ? ` · ${ago(it.resolved.at)}` : ''}
                      {it.resolved?.note ? ` — ${it.resolved.note}` : ''}
                    </span>
                    {actErr?.id === it.id && <span className="block text-[11px] text-red-700">{actErr.msg}</span>}
                  </span>
                  <button onClick={() => undo(it)} disabled={busy === it.id}
                    title="Put it back in the banner and the counts"
                    className="shrink-0 mt-0.5 text-[11px] font-medium text-grayscale-1200 underline disabled:opacity-50">
                    {busy === it.id ? '…' : 'Undo'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

/** Self-contained sign-in card (password or emailed code). */
const AdminSignIn: React.FC<{ onSignedIn: () => void }> = ({ onSignedIn }) => {
  const [f, setF] = useState({ email: '', password: '', otp: '', mode: 'password' as 'password' | 'otp', sent: false, busy: false });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const store = (d: any) => {
    apiService.setStoredAccessToken(d.access_token);
    apiService.setStoredRefreshToken(d.refresh_token);
    apiService.setStoredUserId(d.user_id);
  };
  const password = async () => {
    setF(l => ({ ...l, busy: true })); setErr('');
    const r = await apiService.loginWithPassword(f.email.trim(), f.password);
    setF(l => ({ ...l, busy: false }));
    if (r.success && r.data?.access_token) { store(r.data); onSignedIn(); }
    else setErr(r.error || 'Sign in failed — check your email and password.');
  };
  const sendOtp = async () => {
    setF(l => ({ ...l, busy: true })); setErr('');
    const r = await apiService.sendOTP(f.email.trim(), 'login');
    setF(l => ({ ...l, busy: false, sent: r.success }));
    if (r.success) setMsg('Code sent — check your email.');
    else setErr(r.error || 'Could not send the code');
  };
  const verifyOtp = async () => {
    setF(l => ({ ...l, busy: true })); setErr('');
    const r = await apiService.verifyOTP(f.email.trim(), f.otp.trim(), 'login');
    setF(l => ({ ...l, busy: false }));
    if (r.success && r.data?.access_token) { store(r.data); onSignedIn(); }
    else setErr(r.error || 'That code did not work');
  };

  return (
    <div className="min-h-screen bg-grayscale-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-custom-light p-8 max-w-sm w-full flex flex-col gap-3">
        <h1 className="text-xl font-bold text-grayscale-1300 font-grotesk">Admin sign in</h1>
        <p className="text-sm text-grayscale-1200 -mt-1">
          Use your normal Cosmos account. Admin access is granted per email.
        </p>
        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{msg}</div>}
        <input type="email" autoFocus placeholder="you@universitytech.io" value={f.email}
          onChange={e => setF(l => ({ ...l, email: e.target.value }))}
          className="h-11 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
        {f.mode === 'password' ? (
          <>
            <input type="password" placeholder="Password" value={f.password}
              onChange={e => setF(l => ({ ...l, password: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && password()}
              className="h-11 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
            <button onClick={password} disabled={f.busy || !f.email || !f.password}
              className="h-11 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
              {f.busy ? 'Signing in…' : 'Sign in'}
            </button>
            <button onClick={() => setF(l => ({ ...l, mode: 'otp' }))}
              className="text-xs text-primary-700 underline self-center">Email me a code instead</button>
          </>
        ) : (
          <>
            {f.sent && (
              <input placeholder="6-digit code" value={f.otp}
                onChange={e => setF(l => ({ ...l, otp: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && verifyOtp()}
                className="h-11 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm tracking-widest" />
            )}
            <button onClick={f.sent ? verifyOtp : sendOtp}
              disabled={f.busy || !f.email || (f.sent && !f.otp)}
              className="h-11 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
              {f.busy ? 'Working…' : f.sent ? 'Verify code' : 'Send code'}
            </button>
            <button onClick={() => setF(l => ({ ...l, mode: 'password', sent: false }))}
              className="text-xs text-primary-700 underline self-center">Use password instead</button>
          </>
        )}
        <a href="/" className="text-xs text-grayscale-1100 underline self-center mt-1">Back to Cosmos</a>
      </div>
    </div>
  );
};

const AdminPage: React.FC = () => {
  const [gate, setGate] = useState<Gate>('checking');
  const [email, setEmail] = useState('');
  const [groups, setGroups] = useState<ViewGroup[]>([]);
  const [hidden, setHidden] = useState<string[]>([]);
  const [access, setAccess] = useState<AdminAccess>(OPEN_ACCESS);
  // No panel renders before admin/views answers: a deep link to a group this
  // admin may not open must not fire its requests first.
  const [viewsReady, setViewsReady] = useState(false);
  const [active, setActive] = useState<string>('');
  const [customizing, setCustomizing] = useState(false);
  const [err, setErr] = useState('');
  const [alerts, setAlerts] = useState<AlertFeed | null>(null);
  const [alertsErr, setAlertsErr] = useState('');
  const [seen, setSeen] = useState<Set<string>>(loadSeen);
  const [bellOpen, setBellOpen] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  // Set by the banner's Review button, handed to UsersPanel once, then cleared.
  const [usersTab, setUsersTab] = useState<string | undefined>(undefined);
  // Same for Billing: a rejected offer id opens Promotions, a refund opens
  // Cancellations & refunds — whichever sub-tab the feed put on link.tab.
  const [billingTab, setBillingTab] = useState<string | undefined>(undefined);
  // The deep link in the URL (group + whatever the panel understands), with a
  // nonce per navigation so clicking the same link twice still re-applies it.
  const location = useLocation();
  const navigate = useNavigate();
  const [target, setTarget] = useState<AdminTargetIn | null>(null);
  const activeRef = useRef('');
  activeRef.current = active;

  useEffect(() => {
    const t = parseAdminTarget(location.search);
    if (!t) return;
    setActive(t.group);
    setTarget({ ...t, nonce: `${location.key || ''}:${Date.now()}` });
  }, [location.key, location.search]);

  // A panel asked to open another group (adminNav.openAdmin): label the entry
  // we are leaving with the current group, so Back comes back here, then push
  // the new deep link — the effect above does the rest.
  useEffect(() => {
    const onNav = (e: Event) => {
      const t = (e as CustomEvent<AdminTarget>).detail;
      if (!t || !t.group) return;
      e.preventDefault();
      if (activeRef.current) navigate(adminHref({ group: activeRef.current }), { replace: true });
      navigate(adminHref(t));
    };
    document.addEventListener(ADMIN_NAV_EVENT, onNav);
    return () => document.removeEventListener(ADMIN_NAV_EVENT, onNav);
  }, [navigate]);

  /** Switch group from the shell's own controls, keeping the URL in step so a
   *  reload (or Back) lands on the same group rather than an older deep link. */
  const goGroup = (key: string, replace = false) => {
    setActive(key);
    if (parseAdminTarget(location.search)?.group !== key || location.search.includes('&')) {
      navigate(adminHref({ group: key }), { replace });
    }
  };

  const loadViews = async (uid: string): Promise<boolean> => {
    const v = await apiService.adminViews(uid);
    if (v.success && v.data?.success) {
      const parsed = parseViews(v.data);
      setGroups(parsed.groups);
      setHidden(parsed.hidden);
      setAccess(parsed.access);
      setViewsReady(true);
      setErr('');
      const firstVisible = parsed.groups.find(g => !parsed.hidden.includes(g.key)) || parsed.groups[0];
      setActive(a => a || firstVisible?.key || (parsed.access.legacy ? '' : ACCESS_GROUP.key));
      return true;
    }
    setErr(v.error || 'Could not load the admin views');
    setViewsReady(true);
    return false;
  };

  const checkAccess = async () => {
    const uid = apiService.getStoredUserId() || '';
    if (!uid) { setGate('signed_out'); return; }
    const res = await apiService.adminBillingCheck(uid);
    if (!res.success || !res.data?.signed_in) { setGate('signed_out'); return; }
    if (!res.data.is_admin) { setGate('denied'); setEmail(res.data.email || ''); return; }
    setEmail(res.data.email);
    setGate('ok');
    await loadViews(uid);
  };

  useEffect(() => { checkAccess(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Re-read access when it may have changed: a super admin saved a rule, or
  // the server refused a section (403 admin_tab_not_enabled — apiService).
  // At most once every 3 s: a panel may fire several refused calls at once.
  useEffect(() => {
    if (gate !== 'ok') return;
    let last = 0;
    const onChange = () => {
      const now = Date.now();
      if (now - last < 3000) return;
      last = now;
      const uid = apiService.getStoredUserId() || '';
      if (uid) void loadViews(uid);
    };
    window.addEventListener(ADMIN_ACCESS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(ADMIN_ACCESS_CHANGED_EVENT, onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate]);

  // An old link to Access control → Features takes a super admin to For You
  // & What's New → Feature access (anyone else lands on Admins & access).
  useEffect(() => {
    if (!viewsReady || !access.superAdmin || !target) return;
    if (target.group === ACCESS_GROUP.key && target.tab === OLD_FEATURES_TAB) {
      navigate(adminHref(FEATURE_ACCESS_TARGET), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce, viewsReady, access.superAdmin]);

  // The alerts feed (deletion requests, refunds, ghost mandates, failed
  // cancels, rejected payments, …): one cheap query a minute while the tab
  // is visible, so a refund issued while the admin sits in Users still shows
  // up without a reload. A failed poll keeps the last good payload — an
  // empty banner after a network blip would look like "nothing pending".
  useEffect(() => {
    if (gate !== 'ok') return;
    let dead = false;
    const poll = async (fresh = false) => {
      if (document.visibilityState === 'hidden') return;
      // refresh=1 skips the server's short cache of the expensive orphan
      // scan — right after an admin acted is when stale would be wrong.
      const { data, error } = await panelGet('alerts', 'feed', fresh ? { refresh: 1 } : {});
      if (dead) return;
      if (data && Array.isArray(data.items)) { setAlerts(data); setAlertsErr(''); }
      else setAlertsErr(error || 'Request failed');
    };
    const timed = () => poll(false);
    const forced = () => poll(true);
    poll();
    const t = setInterval(timed, 60000);
    // Coming back to the tab polls at once rather than up to a minute later.
    const onVis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVis);
    // Panels fire these after an action that changes the feed (Hold / Resume /
    // Cancel / Delete now on the deletion tab, refunds and cancels in Billing)
    // so the banner does not contradict the table for up to a minute. The
    // older deletion-only name is kept because the deletion tab still sends it.
    document.addEventListener('utech:alerts-changed', forced);
    document.addEventListener('utech:deletion-alerts-changed', forced);
    return () => {
      dead = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      document.removeEventListener('utech:alerts-changed', forced);
      document.removeEventListener('utech:deletion-alerts-changed', forced);
    };
  }, [gate]);

  // Forget "seen" ids the feed no longer carries, so the store cannot grow
  // for ever — only on a complete feed: a partial one (a source failed) is
  // missing items that are still open, and they must come back as seen.
  useEffect(() => {
    if (!alerts || (alerts.errors && alerts.errors.length)) return;
    const live = new Set(alerts.items.map(it => it.id));
    setSeen(prev => {
      const next = new Set(Array.from(prev).filter(id => live.has(id)));
      if (next.size === prev.size) return prev;
      saveSeen(next);
      return next;
    });
  }, [alerts]);

  // Clear the handed-over sub-tab right after UsersPanel has seen it (child
  // effects run first), so the admin can change tabs freely afterwards and a
  // second Review click is a fresh change the panel reacts to.
  useEffect(() => { if (usersTab) setUsersTab(undefined); }, [usersTab]);
  useEffect(() => { if (billingTab) setBillingTab(undefined); }, [billingTab]);

  const toggleHidden = async (key: string) => {
    const uid = apiService.getStoredUserId() || '';
    const next = hidden.includes(key) ? hidden.filter(k => k !== key) : [...hidden, key];
    // An admin page with zero groups looks broken: keep one.
    if (!hidden.includes(key) && groups.every(g => g.key === key || hidden.includes(g.key))) {
      setErr('At least one group must stay visible.');
      return;
    }
    const r = await apiService.adminSetViews(uid, next);
    if (r.success && r.data?.success) {
      setHidden(r.data.hidden);
      // Never leave the admin staring at a group they just hid.
      if (r.data.hidden.includes(active)) {
        const firstVisible = groups.find(g => !r.data.hidden.includes(g.key));
        if (firstVisible) goGroup(firstVisible.key, true);
      }
    } else setErr(r.error || 'Could not save view preferences');
  };

  if (gate === 'checking') {
    return <div className="min-h-screen bg-grayscale-100 flex items-center justify-center text-sm text-grayscale-1100">Checking access…</div>;
  }
  if (gate === 'signed_out') return <AdminSignIn onSignedIn={checkAccess} />;
  if (gate === 'denied') {
    return (
      <div className="min-h-screen bg-grayscale-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-custom-light p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-grayscale-1300 font-grotesk mb-2">Access denied</h1>
          <p className="text-sm text-grayscale-1200 mb-4">
            <span className="font-medium">{email || 'This account'}</span> is not an admin.
            Ask an existing admin to add you from /admin → Billing → Admins.
          </p>
          <button
            onClick={() => { apiService.clearStoredAuth(); setGate('signed_out'); }}
            className="py-2.5 px-5 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100">
            Sign in with another account
          </button>
        </div>
      </div>
    );
  }

  const visible = groups.filter(g => !hidden.includes(g.key) && g.key !== ACCESS_GROUP.key);
  const hiddenCount = hidden.filter(k => groups.some(g => g.key === k)).length;
  // Admins & access: every admin (changes are super admins only), always at
  // the bottom of the nav. A server without access control has none.
  const accessGroup: ViewGroup | null = !access.legacy
    ? (groups.find(g => g.key === ACCESS_GROUP.key) || ACCESS_GROUP) : null;
  const paletteViews = accessGroup ? [...groups.filter(g => g.key !== ACCESS_GROUP.key), accessGroup] : groups;
  const activeGroup = active === ACCESS_GROUP.key ? accessGroup || undefined : groups.find(g => g.key === active);
  const activeTitle = activeGroup?.title || STATIC_TITLES[active] || active;
  // May this admin open the group in view? A server without access control
  // (legacy) leaves every panel open, as before — but has no Access control.
  const canOpenActive = active === ACCESS_GROUP.key ? !!accessGroup
    : access.legacy || access.superAdmin || groups.some(g => g.key === active);
  // Alerts about a group this admin cannot open stay out of the banner and bell.
  const alertOk = (it: AlertItem) => access.legacy || access.superAdmin
    || groups.some(g => g.key === (it.link?.group || it.group));
  const shownAlerts: AlertFeed | null = alerts && { ...alerts, items: alerts.items.filter(alertOk) };
  // Which groups have something red open — the badge colour, not the count.
  // (Plain computation, not a hook: this sits below the gate's early returns.)
  const redGroups = new Set(
    (shownAlerts?.items || []).filter(it => it.severity === 'red' && !it.resolved).map(it => it.link?.group || it.group));
  const markSeen = (ids: string[]) => setSeen(prev => {
    const next = new Set(prev);
    ids.forEach(id => next.add(id));
    saveSeen(next);
    return next;
  });
  const markAllSeen = () => markSeen((shownAlerts?.items || []).map(it => it.id));
  // Jump to where the item can be dealt with: the Users group's Deletion
  // requests sub-tab for a deletion (as the old banner's Review did), the
  // Billing sub-tab the feed names for anything billing — Promotions for a
  // rejected offer id, Cancellations & refunds for a refund. An item about one
  // account (a contact-abuse flag) opens that account's drawer instead.
  const openAlert = (it: AlertItem) => {
    const g = it.link?.group || it.group;
    if (it.link?.user || it.link?.email) {
      // The whole link goes to the URL (users deep link: &user=, with the
      // tab it opens over); the URL effect above switches group and hands
      // the target to the panel, which opens the drawer.
      navigate(adminHref({ ...it.link, group: g }));
      markSeen([it.id]);
      setBellOpen(false);
      return;
    }
    if (g === 'users' && it.link?.tab) setUsersTab(it.link.tab);
    if (g === 'billing' && it.link?.tab) setBillingTab(it.link.tab);
    goGroup(g);
    markSeen([it.id]);
    setBellOpen(false);
  };
  const refreshAlerts = () => document.dispatchEvent(new Event('utech:alerts-changed'));
  // Resolve / undo for everyone. The item flips here at once; the forced
  // re-read that follows is what the server actually holds.
  const flipResolved = (id: string, resolved: AlertItem['resolved']) => setAlerts(prev => prev && ({
    ...prev, items: prev.items.map(x => (x.id === id ? { ...x, resolved } : x)),
  }));
  const resolveAlert = async (it: AlertItem, note: string): Promise<string> => {
    const { error } = await panelPost('alerts', 'resolve',
      { id: it.id, fingerprint: it.fingerprint, title: it.title, note });
    if (error) return error;
    flipResolved(it.id, { by: 'you', at: new Date().toISOString(), note });
    setResolving(null);
    refreshAlerts();
    return '';
  };
  const unresolveAlert = async (it: AlertItem): Promise<string> => {
    const { error } = await panelPost('alerts', 'unresolve', { id: it.id });
    if (error) return error;
    flipResolved(it.id, undefined);
    refreshAlerts();
    return '';
  };

  return (
    // Groups live in a left rail; each panel keeps its own sub-tabs along the
    // top. Twelve groups had outgrown a horizontal strip — the last few sat
    // behind a scroll and were easy to miss entirely.
    <AdminAccessProvider value={access}>
    <div className="min-h-screen bg-grayscale-100 lg:flex">
      <aside className="lg:w-60 lg:shrink-0 lg:h-screen lg:sticky lg:top-0 bg-white
                        border-b lg:border-b-0 lg:border-r border-grayscale-300
                        flex flex-col gap-3 p-4 lg:overflow-y-auto">
        <div>
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-xl font-bold text-grayscale-1300 font-grotesk">UTech Admin</h1>
            <AlertsBell feed={shownAlerts} seen={seen} open={bellOpen} setOpen={setBellOpen}
              onOpen={openAlert} onMarkAllSeen={markAllSeen} onRefresh={refreshAlerts}
              err={alertsErr} resolving={resolving} setResolving={setResolving}
              onResolve={resolveAlert} onUnresolve={unresolveAlert} />
          </div>
          <div className="text-[11px] text-grayscale-1100 break-words">
            Signed in as {email}
          </div>
          {access.role === 'team' && (
            <div className="mt-1 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-[2px]
                            text-[10px] font-semibold text-amber-800"
              title="A team account can open these pages but cannot change anything">
              Team account · read-only
            </div>
          )}
          <a className="text-[11px] text-primary-700 underline" href="/">back to Cosmos</a>
          {/* Global search (admin/CommandPalette.tsx): pages and records, also on Cmd/Ctrl+K. */}
          <button type="button" onClick={openCommandPalette}
            aria-keyshortcuts="Meta+K Control+K" title={`Search pages and records (${PALETTE_SHORTCUT})`}
            className="mt-2 w-full h-9 px-3 rounded-md border border-transparent bg-grayscale-200
                       hover:border-primary-300 flex items-center justify-between gap-2
                       text-sm text-grayscale-1100 font-grotesk">
            <span>Search…</span>
            <kbd className="rounded bg-white shadow-sm px-1.5 py-0.5 text-[10px] font-sans text-grayscale-1100">
              {PALETTE_SHORTCUT}
            </kbd>
          </button>
        </div>

        {/* Vertical on desktop; on a narrow screen it lies down into a
            horizontally scrolling strip rather than eating the whole viewport. */}
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible -mx-1 px-1">
          {visible.map(g => (
            <button key={g.key} onClick={() => goGroup(g.key)}
              title={g.description}
              className={`text-left py-2 px-3 rounded-lg text-sm font-grotesk whitespace-nowrap
                lg:whitespace-normal transition-colors ${active === g.key
                ? 'bg-primary-100 text-primary-700 font-bold'
                : 'text-grayscale-1200 hover:bg-grayscale-200'}`}>
              {g.title}
              {(alerts?.groups?.[g.key] || 0) > 0 && (
                <span className={`ml-1.5 inline-block align-middle rounded-full text-white text-[10px] font-bold px-1.5 leading-4
                  ${redGroups.has(g.key) ? 'bg-red-600' : 'bg-amber-500'}`}
                  title={`${alerts?.groups?.[g.key]} open alert(s) in ${g.title}`}>
                  {alerts?.groups?.[g.key]}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="lg:mt-auto flex flex-col gap-2">
          {accessGroup && (
            <button onClick={() => goGroup(accessGroup.key)} title={accessGroup.description}
              className={`w-full text-left py-2 px-3 rounded-lg text-sm font-grotesk flex items-center gap-2
                border transition-colors ${active === accessGroup.key
                ? 'bg-primary-100 text-primary-700 font-bold border-primary-300'
                : 'text-grayscale-1200 border-grayscale-300 hover:bg-grayscale-200'}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              <span className="flex-1">{accessGroup.title}</span>
            </button>
          )}
          <button onClick={() => setCustomizing(true)}
            className="w-full py-1.5 px-3 rounded-full text-xs border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200">
            Customize views{hiddenCount ? ` (${hiddenCount} hidden)` : ''}
          </button>
        </div>
      </aside>

      <CommandPalette views={paletteViews} hidden={hidden} />

      {customizing && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setCustomizing(false)}>
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md max-h-[80vh] flex flex-col"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-5 pb-3">
              <div>
                <h3 className="text-base font-bold text-grayscale-1300 font-grotesk">
                  Customize views
                </h3>
                <p className="text-xs text-grayscale-1100 mt-0.5">
                  Groups you see — only changes YOUR view.
                </p>
              </div>
              <button onClick={() => setCustomizing(false)} aria-label="Close"
                className="shrink-0 px-2 py-1 rounded-full text-grayscale-1100 hover:bg-grayscale-200 text-lg leading-none">
                ×
              </button>
            </div>
            <div className="px-5 overflow-y-auto">
              {groups.filter(g => g.key !== ACCESS_GROUP.key).map(g => (
                <label key={g.key} className="flex items-start gap-2 py-2 text-sm cursor-pointer">
                  <input type="checkbox" className="mt-0.5"
                    checked={!hidden.includes(g.key)}
                    onChange={() => toggleHidden(g.key)} />
                  <span>
                    <span className="text-grayscale-1300">{g.title}</span>
                    <span className="block text-[11px] text-grayscale-1100">{g.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="p-5 pt-3 flex justify-end">
              <button onClick={() => setCustomizing(false)}
                className="py-2 px-5 rounded-full bg-primary-500 text-grayscale-100 text-sm font-medium">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* min-w-0 matters: without it a wide table inside a flex child refuses
          to shrink and pushes the whole page into a horizontal scroll. */}
      <main className="flex-1 min-w-0 p-4 md:p-8 flex flex-col gap-4">
        {shownAlerts && (
          <AlertsBanner feed={shownAlerts} seen={seen} onOpen={openAlert}
            onSeeAll={() => setBellOpen(true)} onMarkAllSeen={markAllSeen}
            onResolve={it => { setResolving(it.id); setBellOpen(true); }} />
        )}
        {alertsErr && (
          <div className="text-[11px] text-grayscale-1100">
            Alerts could not be refreshed: {alertsErr}
          </div>
        )}
        <h2 className="text-2xl font-bold text-grayscale-1300 font-grotesk">
          {activeTitle}
        </h2>
        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        {!viewsReady ? (
          <div className="text-sm text-grayscale-1100 py-6 text-center">Loading…</div>
        ) : !active ? null : !canOpenActive ? (
          <NotEnabled what={activeTitle} />
        ) : (
          <GroupContent groupKey={active} title={activeTitle} email={email}
            usersTab={usersTab} billingTab={billingTab}
            target={target && target.group === active ? target : undefined} />
        )}
      </main>
    </div>
    </AdminAccessProvider>
  );
};

/** Each group's migrated content. Billing embeds the previously standalone page. */
const GroupContent: React.FC<{
  groupKey: string; title: string; email: string; usersTab?: string; billingTab?: string;
  /** The URL's deep link, when it is for this group (see admin/adminNav.tsx). */
  target?: AdminTargetIn;
}> = ({ groupKey, title, email, usersTab, billingTab, target }) => {
  switch (groupKey) {
    case 'users': return <UsersPanel initialTab={usersTab} target={target} />;
    case 'user360': return <User360Panel target={target} />;
    case 'tags': return <UserTagsPanel target={target} />;
    case 'analytics': return <AnalyticsPanel />;
    case 'search_intel': return <SearchIntelPanel />;
    case 'homepage': return <HomepagePanel target={target} />;
    case 'leads': return <LeadsPanel target={target} />;
    case 'email': return <EmailPanel target={target} />;
    case 'content': return <ContentPanel target={target} />;
    case 'universities': return <UniversitiesPanel />;
    case 'chat': return <ChatStatsPanel />;
    case 'labs': return <LabsPanel />;
    case 'posthog': return <PostHogPanel />;
    case 'database': return <DatabasePanel />;
    case 'ocean': return <OceanPanel target={target} />;
    // A deep link's tab (/admin?group=billing&tab=prompts) as well as an alert's.
    case 'billing': return <AdminBillingPage embedded adminEmail={email} initialTab={billingTab || target?.tab} />;
    case 'funnel': return <FunnelPanel />;
    case 'onboarding': return <OnboardingPanel target={target} />;
    case 'foryou_whatsnew': return <ForYouWhatsNewPanel target={target} />;
    case 'ip_intel': return <IpIntelPanel target={target} />;
    case 'tech_pages': return <TechPagesPanel target={target} />;
    case 'horizon': return <HorizonPanel target={target} />;
    case 'aws': return <AwsPanel target={target} />;
    // Old API spend deep links: the group became AWS & API spend → API spend.
    case 'spend': return <AwsPanel initialTab="API spend" />;
    case 'recrawl': return <RecrawlPanel target={target} />;
    case 'system': return <SystemPanel target={target} />;
    case 'audit': return <AuditPanel target={target} />;
    case 'access': return <AccessControlPanel target={target} />;
    default:
      return (
        <div className="rounded-xl border border-grayscale-300 bg-white p-8 text-center">
          <div className="text-sm font-bold text-grayscale-1300 font-grotesk mb-1">{title}</div>
          <p className="text-sm text-grayscale-1200">Unknown group.</p>
        </div>
      );
  }
};

export default AdminPage;
